import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp, Smile, Database, Settings, User, BookOpen } from "lucide-react";

const OPPORTUNITY_CARDS = [
  {
    key: "revenue",
    icon: TrendingUp,
    color: "#0D9488",
    bg: "#E6FAF6",
    title: "Revenue & Growth",
    sub: "Find, win and grow",
    highlight: true, // Glowing cyan border as in screenshot
  },
  {
    key: "cx",
    icon: Smile,
    color: "#9333EA",
    bg: "#F5EEFD",
    title: "Customer Experience",
    sub: "Delight at every touchpoint",
  },
  {
    key: "finance",
    icon: Database,
    color: "#D97706",
    bg: "#FEF7E6",
    title: "Finance",
    sub: "Improve profitability",
  },
  {
    key: "ops",
    icon: Settings,
    color: "#2563EB",
    bg: "#EFF6FF",
    title: "Operations",
    sub: "Do more with less",
  },
  {
    key: "people",
    icon: User,
    color: "#F43F5E",
    bg: "#FEEFF1",
    title: "People",
    sub: "Empower talent",
  },
  {
    key: "knowledge",
    icon: BookOpen,
    color: "#0891B2",
    bg: "#E6FAFC",
    title: "Knowledge & Decisions",
    sub: "Turn insight into action",
  },
];

export const EnterpriseOpportunities = () => {
  const navigate = useNavigate();

  return (
    <section
      className="relative w-full overflow-hidden py-20 lg:py-24"
      style={{ backgroundColor: "rgba(0, 17, 39, 1)" }}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[480px_1fr] xl:grid-cols-[500px_1fr] lg:gap-14">
          
          {/* Left Column: Headlines & CTA */}
          <div className="flex flex-col items-start gap-5">
            <p className="text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#788EA8]">
              OPPORTUNITIES ACROSS YOUR ENTERPRISE
            </p>

            <h2 className="text-[32px] sm:text-[38px] lg:text-[40px] xl:text-[44px] font-bold leading-[1.18] tracking-tight text-white">
              <span className="block whitespace-nowrap">Real possibilities.</span>
              <span className="block whitespace-nowrap">Across your business.</span>
            </h2>

            <p className="max-w-[440px] text-[15px] sm:text-[15.5px] leading-[26px] text-[#A0B3CC]">
              Cognitive AI can create value in every part of your enterprise — from growth to operations and beyond.
            </p>

            <div className="pt-2">
              <button
                onClick={() => navigate("/ai-opportunities?tab=Business+Function")}
                className="group inline-flex items-center gap-2.5 rounded-full border border-white/25 bg-white/[0.03] px-6 py-3 text-[14px] font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white/50 hover:bg-white/10 active:scale-95 cursor-pointer"
              >
                <span>Explore AI Opportunities</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
              </button>
            </div>
          </div>

          {/* Right Column: 6 Cards in 2 columns (Clickable) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-4.5">
            {OPPORTUNITY_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  onClick={() => navigate(`/ai-opportunities?tab=Business+Function&fn=${card.key}`)}
                  className={`group flex items-center justify-between rounded-[20px] bg-white p-5 sm:px-6 sm:py-5 text-left shadow-sm cursor-pointer select-none transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${
                    card.highlight
                      ? "border-2 border-[#00D2EE] shadow-[0_0_24px_rgba(0,210,238,0.32)]"
                      : "border border-white/80 hover:border-white"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span
                      className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-[14px] transition-transform duration-200 group-hover:scale-105"
                      style={{ background: card.bg }}
                    >
                      <Icon className="h-5 w-5" style={{ color: card.color }} strokeWidth={2.3} />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-[16px] font-bold text-[#06152B] leading-tight transition-colors duration-200 group-hover:text-[#007AFF]">
                        {card.title}
                      </span>
                      <span className="text-[13px] text-[#64748B] mt-1 leading-normal">
                        {card.sub}
                      </span>
                    </div>
                  </div>

                  <ArrowRight
                    className="h-4 w-4 text-[#CBD5E1] transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#007AFF]"
                    strokeWidth={2}
                  />
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};

export default EnterpriseOpportunities;
